@file:Suppress("MagicNumber", "ReturnCount")

package top.wcpe.allincore.ui.runtime.animation

import top.wcpe.allincore.dsl.ir.model.UiBinding
import top.wcpe.allincore.dsl.ir.model.animation.UiAnimation
import top.wcpe.allincore.ui.runtime.element.AnimationDriverHandle
import top.wcpe.allincore.ui.runtime.element.FrameTickable
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.event.InputDispatcher
import top.wcpe.allincore.ui.runtime.internal.concurrent.SyncLock
import top.wcpe.allincore.ui.runtime.internal.concurrent.newIdentityMap
import top.wcpe.allincore.ui.runtime.internal.concurrent.withLock
import top.wcpe.allincore.ui.runtime.internal.log.UiLog
import top.wcpe.allincore.ui.runtime.ir.prop.PropAppliers
import kotlin.jvm.JvmField

/**
 * UI 动画调度器。每个 RenderRoot 拥有一个实例。
 *
 * 实现 [AnimationDriverHandle]：UIElement（commonMain）经该中立句柄持有本调度器（marker 断环）。
 */
class AnimationScheduler : AnimationDriverHandle {

    /** 原 @Synchronized（锁 this）方法的平台中立替代：类内单锁互斥（语义同粗粒度对象锁）。 */
    private val syncMonitor = SyncLock()

    private val logger = UiLog("top.wcpe.allincore.ui.runtime.animation.AnimationScheduler")
    private val active: MutableList<ActiveAnimation> = ArrayList()
    private val pendingMount: MutableList<UIElement<*>> = ArrayList()
    private val owners: MutableMap<UIElement<*>, MutableMap<String, ActiveAnimation>> = newIdentityMap()
    private val idleElements: MutableList<UIElement<*>> = ArrayList()
    private val frameTickables: MutableList<FrameTickable> = ArrayList()
    private val idleFired: MutableMap<UIElement<*>, Boolean> = newIdentityMap()
    private var currentTimeMs: Long = 0L
    private var disposed: Boolean = false
    private var globalPaused: Boolean = false
    private var globalPausedAtMs: Long = -1L

    companion object {
        const val IDLE_THRESHOLD_MS = 3000L
        const val LOOP_MODE_INFINITE = "true"
        const val LOOP_MODE_MIRROR = "mirror"
        const val TRIGGER_ANIMATION = "animation"
        const val TRIGGER_ENTER = "enter"
        const val TRIGGER_IDLE = "idle"
        const val MAX_SPRING_DT_SEC = 0.1
    }

    fun attach(root: UIElement<*>) {
        syncMonitor.withLock {
            attachRecursive(root)
        }
    }

    override fun attachSubTree(root: UIElement<*>) {
        syncMonitor.withLock {
            attachSubTreeRecursive(root)
        }
    }

    // 中立句柄交互动画方法（AnimationDriverHandle）：委托既有 play / cancelByTrigger，
    // 供 commonMain 的 InputDispatcher 在 press/hover/release 时按 trigger 驱动动画。
    override fun playTrigger(element: UIElement<*>, trigger: String) {
        play(element, trigger)
    }

    override fun cancelTrigger(element: UIElement<*>, trigger: String) {
        cancelByTrigger(element, trigger)
    }

    private fun attachSubTreeRecursive(element: UIElement<*>) {
        element.attachAnimationScheduler(this)
        registerScrollContainer(element)
        if (element.animationFor(TRIGGER_ANIMATION) != null) pendingMount.add(element)
        if (element.animationFor(TRIGGER_ENTER) != null) pendingMount.add(element)
        if (element.animationFor(TRIGGER_IDLE) != null) {
            idleElements.add(element)
            idleFired[element] = false
        }
        for (child in element.children()) attachSubTreeRecursive(child)
    }

    private fun attachRecursive(element: UIElement<*>) {
        element.attachAnimationScheduler(this)
        registerScrollContainer(element)
        if (element.animationFor(TRIGGER_ANIMATION) != null) pendingMount.add(element)
        if (element.animationFor(TRIGGER_ENTER) != null) pendingMount.add(element)
        if (element.animationFor(TRIGGER_IDLE) != null) {
            idleElements.add(element)
            idleFired[element] = false
        }
        for (child in element.children()) attachRecursive(child)
    }

    private fun registerScrollContainer(element: UIElement<*>) {
        if (element is FrameTickable && element !in frameTickables) frameTickables.add(element)
    }

    fun play(element: UIElement<*>, trigger: String): AnimationHandle? = syncMonitor.withLock {
        element.animationFor(trigger)?.let { play(element, it, currentTimeMs) }
    }

    fun playDirect(element: UIElement<*>, animation: UiAnimation?): AnimationHandle? = syncMonitor.withLock {
        animation?.let { play(element, it, currentTimeMs) }
    }

    fun play(element: UIElement<*>, animation: UiAnimation?, startMs: Long): AnimationHandle? {
        syncMonitor.withLock {
            if (disposed || element == null || animation == null) return null
            val props = AnimationEngine.collectProps(animation)
            val starts: MutableMap<String, Any?> = LinkedHashMap()
            for (prop in props) starts[prop] = AnimationEngine.currentValue(element, prop)
            val activeAnim = ActiveAnimation(element, animation, startMs, props, starts)
            val handle = addActive(activeAnim)
            applyInitialFrame(activeAnim, animation, starts)
            return handle
        }
    }

    private fun applyInitialFrame(
        activeAnim: ActiveAnimation,
        animation: UiAnimation,
        starts: MutableMap<String, Any?>,
    ) {
        val springConfig = animation.spring
        if (springConfig != null) {
            val spring = Spring(springConfig)
            activeAnim.spring = spring
            AnimationEngine.applySpringNode(activeAnim, spring, 0.0)
            return
        }
        AnimationEngine.applyNode(activeAnim, animation, 0.0, starts)
    }

    fun isPlaying(element: UIElement<*>): Boolean = syncMonitor.withLock { active.any { it.element === element } }

    fun transition(
        element: UIElement<*>,
        prop: String?,
        from: Any?,
        to: Any?,
        template: UiAnimation?,
    ): AnimationHandle? = syncMonitor.withLock { transition(element, prop, from, to, template, currentTimeMs) }

    fun transition(
        element: UIElement<*>,
        prop: String?,
        from: Any?,
        to: Any?,
        template: UiAnimation?,
        startMs: Long,
    ): AnimationHandle? {
        syncMonitor.withLock {
            if (disposed || template == null) return null
            if (element == null || prop == null) return null
            val props: MutableMap<String, UiBinding> = LinkedHashMap()
            props[prop] = UiBinding.literal(to)
            val animation = UiAnimation(
                UiAnimation.Kind.TRANSITION, template.durationMs, template.delayMs,
                template.staggerMs, template.easing, props,
                emptyList(), emptyList(), null, template.spring,
            )
            val starts: MutableMap<String, Any?> = LinkedHashMap()
            starts[prop] = from
            val touched: MutableSet<String> = LinkedHashSet()
            touched.add(prop)
            val activeAnim = ActiveAnimation(element, animation, startMs, touched, starts)
            val handle = addActive(activeAnim)
            applyInitialFrame(activeAnim, animation, starts)
            return handle
        }
    }

    private fun addActive(animation: ActiveAnimation): AnimationHandle {
        var byProp = owners.getOrPut(animation.element) { LinkedHashMap() }
        for (prop in animation.props) {
            val old = byProp[prop] ?: continue
            old.props.remove(prop)
            byProp.remove(prop)
            if (old.props.isEmpty()) {
                old.handle.cancel()
                remove(old)
            }
        }
        byProp = owners.getOrPut(animation.element) { LinkedHashMap() }
        for (prop in animation.props) byProp[prop] = animation
        active.add(animation)
        animation.handle.scheduler = this
        animation.handle.active = animation
        return animation.handle
    }

    fun tick(nowMs: Long) {
        syncMonitor.withLock {
            currentTimeMs = nowMs
            if (globalPaused || disposed) return
            if (pendingMount.isNotEmpty()) {
                val mounts = ArrayList(pendingMount)
                pendingMount.clear()
                for (element in mounts) {
                    element.animationFor(TRIGGER_ANIMATION)?.let { play(element, it, nowMs) }
                    element.animationFor(TRIGGER_ENTER)?.let { play(element, it, nowMs) }
                }
            }
            checkIdleTrigger(nowMs)
            tickActiveAnimations(nowMs)
            tickFrameTickables(nowMs)
        }
    }

    private fun tickFrameTickables(nowMs: Long) {
        if (frameTickables.isEmpty()) return
        val snapshot = ArrayList(frameTickables)
        for (t in snapshot) {
            try {
                t.tickFrame(nowMs)
                // FR-30 B4：帧驱动元素每帧改视觉，标渲染脏（兜底未经 PropAppliers 的帧更新）
                if (t is UIElement<*>) {
                    t.markRenderDirty()
                }
            } catch (ex: Throwable) {
                logger.warn("[ui] 帧驱动元素 tickFrame 异常已隔离并移除该元素；元素=${t::class.simpleName}", ex)
                frameTickables.remove(t)
            }
        }
    }

    private fun tickActiveAnimations(nowMs: Long) {
        val snapshot = ArrayList(active)
        for (animation in snapshot) {
            try {
                tickOneActiveAnimation(animation, nowMs)
                // FR-30 B4：动画推进改元素视觉，标渲染脏（兜底 spring / slider 等未经 PropAppliers 的路径）
                animation.element.markRenderDirty()
            } catch (ex: Throwable) {
                logger.warn(
                    "[ui] 动画推进异常已隔离并移除该动画；元素=${animation.element?.let {
                        it::class.simpleName
                    } ?: "null"} 属性=${animation.props}",
                    ex,
                )
                animation.handle.cancel()
                remove(animation)
            }
        }
    }

    private fun tickOneActiveAnimation(animation: ActiveAnimation, nowMs: Long) {
        if (animation.handle.isCancelled()) {
            remove(animation)
            return
        }
        if (animation.animation.spring != null) {
            tickSpringActive(animation, nowMs)
            return
        }
        val referenceNow = if (animation.pausedAt >= 0) animation.pausedAt else nowMs
        var elapsed = (referenceNow - animation.startMs - animation.pauseAccum).toDouble()
        if (elapsed < 0.0) elapsed = 0.0
        val total = AnimationEngine.totalDuration(animation.animation)
        val loopMode = animation.animation.loopMode
        if (!loopMode.isNullOrEmpty() && elapsed >= total && total > 0) {
            elapsed = when (loopMode) {
                LOOP_MODE_INFINITE -> elapsed % total
                LOOP_MODE_MIRROR -> {
                    val cycles = (elapsed / total).toLong()
                    val remainder = elapsed % total
                    if (cycles % 2 == 0L) remainder else total - remainder
                }
                else -> elapsed
            }
        }
        AnimationEngine.applyNode(animation, animation.animation, elapsed, animation.startValues)
        if (loopMode.isNullOrEmpty() && elapsed >= total) {
            animation.handle.finish()
            remove(animation)
        }
    }

    private fun tickSpringActive(animation: ActiveAnimation, nowMs: Long) {
        if (animation.pausedAt >= 0) {
            animation.lastSpringTickMs = -1L
            return
        }
        if (animation.spring == null) {
            animation.spring = Spring(animation.animation.spring ?: return)
        }
        val spring = animation.spring ?: return
        val dtSec = if (animation.lastSpringTickMs < 0) {
            0.0
        } else {
            minOf((nowMs - animation.lastSpringTickMs) / 1000.0, MAX_SPRING_DT_SEC)
        }
        animation.lastSpringTickMs = nowMs
        val allRest = AnimationEngine.applySpringNode(animation, spring, dtSec)
        if (allRest) {
            animation.handle.finish()
            remove(animation)
        }
    }

    fun activeCount(): Int = syncMonitor.withLock { active.size }

    fun pauseAnimation(handle: AnimationHandle?) {
        syncMonitor.withLock {
            val a = handle?.active ?: return
            if (handle.isCancelled() || handle.isFinished() || a.pausedAt >= 0) return
            a.pausedAt = currentTimeMs
            handle.setPaused(true)
        }
    }

    fun resumeAnimation(handle: AnimationHandle?) {
        syncMonitor.withLock {
            val a = handle?.active ?: return
            if (a.pausedAt < 0) return
            val delta = currentTimeMs - a.pausedAt
            if (delta > 0) a.pauseAccum += delta
            a.pausedAt = -1L
            handle.setPaused(false)
        }
    }

    fun seekAnimation(handle: AnimationHandle?, t01: Double) {
        syncMonitor.withLock {
            val a = handle?.active ?: return
            if (handle.isCancelled() || handle.isFinished()) return
            val t = t01.coerceIn(0.0, 1.0)
            val total = AnimationEngine.totalDuration(a.animation)
            val referenceNow = if (a.pausedAt >= 0) a.pausedAt else currentTimeMs
            a.startMs = referenceNow - (t * total).toLong() - a.pauseAccum
        }
    }

    fun stopAnimation(handle: AnimationHandle?) {
        syncMonitor.withLock {
            val a = handle?.active
            if (a == null) {
                handle?.cancel()
                return
            }
            for (prop in a.props) {
                if (a.startValues.containsKey(prop)) {
                    val start = a.startValues[prop]
                    if (!AnimationEngine.applySilentSliderValue(a.element, prop, start)) {
                        PropAppliers.apply(a.element, prop, start)
                    }
                }
            }
            handle.cancel()
            handle.setPaused(false)
            remove(a)
        }
    }

    fun dispose() {
        syncMonitor.withLock {
            disposed = true
            for (animation in active) animation.handle.cancel()
            active.clear()
            pendingMount.clear()
            owners.clear()
            idleElements.clear()
            idleFired.clear()
            frameTickables.clear()
        }
    }

    fun pauseAll() {
        syncMonitor.withLock {
            if (!globalPaused) {
                globalPaused = true
                globalPausedAtMs = currentTimeMs
            }
        }
    }

    fun resumeAll() {
        syncMonitor.withLock {
            resumeAll(currentTimeMs)
        }
    }

    fun resumeAll(nowMs: Long) {
        syncMonitor.withLock {
            if (!globalPaused) return
            if (globalPausedAtMs >= 0) {
                val delta = nowMs - globalPausedAtMs
                for (a in active) if (a.pausedAt < 0) a.pauseAccum += delta
                globalPausedAtMs = -1L
            }
            globalPaused = false
        }
    }

    fun isGlobalPaused(): Boolean = syncMonitor.withLock { globalPaused }

    fun cancelByTrigger(element: UIElement<*>?, trigger: String?) {
        syncMonitor.withLock {
            if (element == null || trigger == null) return
            val target = element.animationFor(trigger) ?: return
            val toCancel = active.filter { it.element === element && it.animation === target }
            for (a in toCancel) {
                a.handle.cancel()
                remove(a)
            }
            if (trigger == TRIGGER_IDLE) idleFired[element] = false
        }
    }

    private fun checkIdleTrigger(nowMs: Long) {
        if (idleElements.isEmpty()) return
        val lastInput = InputDispatcher.getLastInputMs()
        if (lastInput <= 0) return
        val idle = (nowMs - lastInput) >= IDLE_THRESHOLD_MS
        if (!idle) {
            for (el in idleElements) if (idleFired[el] == true) idleFired[el] = false
            return
        }
        for (el in idleElements) {
            if (idleFired[el] != true) {
                idleFired[el] = true
                el.animationFor(TRIGGER_IDLE)?.let { play(el, it, nowMs) }
            }
        }
    }

    private fun remove(animation: ActiveAnimation) {
        active.remove(animation)
        val byProp = owners[animation.element] ?: return
        for (prop in animation.props) if (byProp[prop] === animation) byProp.remove(prop)
        if (byProp.isEmpty()) owners.remove(animation.element)
    }

    class ActiveAnimation(
        @JvmField val element: UIElement<*>,
        @JvmField val animation: UiAnimation,
        @JvmField var startMs: Long,
        @JvmField val props: MutableSet<String>,
        @JvmField val startValues: MutableMap<String, Any?>,
    ) {
        @JvmField
        val nodeStartValues: MutableMap<UiAnimation, MutableMap<String, Any?>> = newIdentityMap()

        @JvmField
        val handle: AnimationHandle = AnimationHandle()

        @JvmField
        var pausedAt: Long = -1L

        @JvmField
        var pauseAccum: Long = 0L

        @JvmField
        var spring: Spring? = null

        @JvmField
        val springState: MutableMap<String, DoubleArray> = LinkedHashMap()

        @JvmField
        var lastSpringTickMs: Long = -1L
    }
}
